import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { formatINR } from "@/lib/format";

function monthLabel(monthStr) {
  if (!monthStr) return "—";
  try {
    const [y, m] = monthStr.split("-");
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return monthStr;
  }
}

export default function GstTab({ propertyId }) {
  const { user } = useAuth();
  const isAdmin = user && user !== false && user.role === "admin";
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/rents`);
      setRecords(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const toggleReceived = async (r, value) => {
    setSavingId(r.id);
    try {
      await api.put(`/properties/${propertyId}/rents/${r.id}`, {
        month: r.month,
        amount: r.amount,
        due_date: r.due_date,
        status: r.status,
        gst_filed: value,
        gst_amount: r.gst_amount,
        gst_receipt_month: r.gst_receipt_month,
        notes: r.notes || "",
      });
      setRecords((curr) => curr.map((x) => (x.id === r.id ? { ...x, gst_filed: value } : x)));
      toast.success(value ? "Marked as received" : "Marked as pending");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const totals = records.reduce(
    (acc, r) => {
      acc.count += 1;
      const gstAmt = Number(r.gst_amount || 0);
      acc.totalGst += gstAmt;
      if (r.gst_filed) {
        acc.received += 1;
        acc.receivedAmt += gstAmt;
      } else {
        acc.pending += 1;
        acc.pendingAmt += gstAmt;
      }
      return acc;
    },
    { count: 0, received: 0, pending: 0, totalGst: 0, receivedAmt: 0, pendingAmt: 0 }
  );

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-medium text-stone-900">GST Status</h2>
        <p className="text-sm text-stone-500 mt-1">
          GST is 18% of rent and is received the month after the rent — once the return is filed.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-stone-200 bg-white p-5 rounded-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Total GST</p>
          <p className="mt-2 text-2xl font-medium text-stone-900">₹{formatINR(Math.round(totals.totalGst))}</p>
          <p className="text-xs text-stone-500 mt-1">{totals.count} months</p>
        </Card>
        <Card className="border-stone-200 bg-white p-5 rounded-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Received</p>
          <p className="mt-2 text-2xl font-medium text-emerald-800">₹{formatINR(Math.round(totals.receivedAmt))}</p>
          <p className="text-xs text-stone-500 mt-1">{totals.received} months</p>
        </Card>
        <Card className="border-stone-200 bg-white p-5 rounded-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Pending</p>
          <p className="mt-2 text-2xl font-medium text-amber-700">₹{formatINR(Math.round(totals.pendingAmt))}</p>
          <p className="text-xs text-stone-500 mt-1">{totals.pending} months</p>
        </Card>
      </div>

      {loading ? (
        <div className="h-40 rounded-xl bg-white border border-stone-200 animate-pulse" />
      ) : records.length === 0 ? (
        <Card className="border border-dashed border-stone-300 bg-white p-12 text-center rounded-xl">
          <ShieldCheck className="h-10 w-10 text-stone-300 mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="text-lg font-medium text-stone-900">No months to track yet</h3>
          <p className="text-stone-500 mt-1 text-sm">
            Add a rent record on the <span className="text-stone-700">Rent</span> tab — it will appear here for GST tracking.
          </p>
        </Card>
      ) : (
        <Card className="border-stone-200 bg-white rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-100 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-stone-400">Period</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-stone-400">GST amount</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-stone-400">Receipt month</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-stone-400">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-stone-400">Received</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r) => (
                <TableRow key={r.id} className="border-stone-100" data-testid={`gst-row-${r.id}`}>
                  <TableCell className="font-medium text-stone-900">{monthLabel(r.month)}</TableCell>
                  <TableCell className="text-stone-700">
                    ₹{formatINR(Math.round(Number(r.gst_amount || r.amount * 0.18)))}
                  </TableCell>
                  <TableCell className="text-stone-600">{monthLabel(r.gst_receipt_month)}</TableCell>
                  <TableCell>
                    {r.gst_filed ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 rounded-full">Received</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100 rounded-full">Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {isAdmin ? (
                      <Switch
                        checked={!!r.gst_filed}
                        disabled={savingId === r.id}
                        onCheckedChange={(v) => toggleReceived(r, v)}
                        data-testid={`gst-switch-${r.id}`}
                      />
                    ) : (
                      <span className="text-stone-300 text-xs">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
